import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { LegalProvider } from './lib/legal-context';
import { AuthProvider } from './lib/auth-context';
import { ConsentProvider } from './lib/consent-context';
import { CookieBanner } from './components/consent/CookieBanner';
import { CookiePreferenceCenter } from './components/consent/CookiePreferenceCenter';
import { RequireAuth } from './components/RequireAuth';

import { Home } from './pages/Home';
import { Services } from './pages/Services';
import { ServiceDetail } from './pages/ServiceDetail';
import { About } from './pages/About';
import { Regulatory } from './pages/Regulatory';
import { Contact } from './pages/Contact';
import { Privacy } from './pages/Privacy';
import { Cookies } from './pages/Cookies';
import { Terms } from './pages/Terms';
import { Gdpr } from './pages/Gdpr';
import { SignIn } from './pages/SignIn';

import { PortalSignUp } from './pages/portal/SignUp';
import { PortalLayout } from './pages/portal/PortalLayout';
import { PortalDashboard } from './pages/portal/Dashboard';
import { PortalStub } from './pages/portal/Stub';
import { NewRequest } from './pages/portal/NewRequest';
import { MyRequests } from './pages/portal/MyRequests';
import { RequestDetail } from './pages/portal/RequestDetail';

import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminOverview } from './pages/admin/Overview';
import { AdminInbox } from './pages/admin/Inbox';
import { AdminRequestDetail } from './pages/admin/RequestDetail';
import { AdminProposals } from './pages/admin/Proposals';
import { AdminProposalDetail } from './pages/admin/ProposalDetail';
import { AdminPipeline } from './pages/admin/Pipeline';
import { AdminClients } from './pages/admin/Clients';
import { AdminClientCreate } from './pages/admin/ClientCreate';
import { AdminClientDetail } from './pages/admin/ClientDetail';
import { AdminUsers } from './pages/admin/Users';
import { AdminAudit } from './pages/admin/Audit';
import { AdminFinance } from './pages/admin/Finance';
import { AdminTransactions } from './pages/admin/Transactions';
import { AdminInvoiceDetail } from './pages/admin/InvoiceDetail';
import { AdminKyc } from './pages/admin/Kyc';
import { ComplianceQueue } from './pages/admin/ComplianceQueue';
import { ComplianceClient } from './pages/admin/ComplianceClient';
import { AdminReports } from './pages/admin/Reports';
import { AdminSupport } from './pages/admin/Support';
import { AdminSupportDetail } from './pages/admin/SupportDetail';
import { PortalProposals } from './pages/portal/Proposals';
import { PortalProposalDetail } from './pages/portal/ProposalDetail';
import { PortalInvoices } from './pages/portal/Invoices';
import { PortalInvoiceDetail } from './pages/portal/InvoiceDetail';
import { PortalMessages } from './pages/portal/Messages';
import { PortalNotifications } from './pages/portal/Notifications';
import { PortalDocuments } from './pages/portal/Documents';
import { PortalCompliance } from './pages/portal/Compliance';
import { PortalSupport } from './pages/portal/Support';
import { PortalSupportDetail } from './pages/portal/SupportDetail';
import { PortalSettings } from './pages/portal/Settings';

// Marketing chrome (Header/Footer) only renders on public pages.
// Portal + Admin sections render their own full-screen shell.
function MarketingChrome({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const isPortalSection = pathname.startsWith('/portal') || pathname.startsWith('/admin');
  // /sign-in renders its own slim auth-only chrome (logo header + copyright
  // footer), so we suppress the full marketing Header + Footer there too.
  const isAuthScreen = pathname === '/sign-in';
  if (isPortalSection || isAuthScreen) return <>{children}</>;
  return (
    <>
      <Header />
      {children}
      <Footer />
    </>
  );
}

export function App() {
  return (
    <AuthProvider>
      <LegalProvider>
        <ConsentProvider>
        <MarketingChrome>
          <Routes>
            {/* Public marketing site */}
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:id" element={<ServiceDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/regulatory" element={<Regulatory />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/gdpr" element={<Gdpr />} />

            {/* Unified sign-in for both clients and staff. After verify-code
                we route the user to /admin or /portal based on their kind. */}
            <Route path="/sign-in" element={<SignIn />} />
            {/* Back-compat redirects — old links + bookmarks still work. */}
            <Route path="/portal/sign-in" element={<Navigate to="/sign-in" replace />} />
            <Route path="/admin/sign-in" element={<Navigate to="/sign-in" replace />} />

            {/* Client portal — sign-up is the only public client route */}
            <Route path="/portal/sign-up" element={<PortalSignUp />} />

            {/* Client portal — authenticated */}
            <Route
              path="/portal"
              element={
                <RequireAuth kind="client" signInTo="/sign-in">
                  <PortalLayout />
                </RequireAuth>
              }
            >
              <Route index element={<PortalDashboard />} />
              <Route path="requests"      element={<MyRequests />} />
              <Route path="requests/new"  element={<NewRequest />} />
              <Route path="requests/:id"  element={<RequestDetail />} />
              <Route path="proposals"     element={<PortalProposals />} />
              <Route path="proposals/:id" element={<PortalProposalDetail />} />
              <Route path="invoices"      element={<PortalInvoices />} />
              <Route path="invoices/:id"  element={<PortalInvoiceDetail />} />
              <Route path="documents"     element={<PortalDocuments />} />
              <Route path="compliance"    element={<PortalCompliance />} />
              <Route path="messages"      element={<PortalMessages />} />
              <Route path="notifications" element={<PortalNotifications />} />
              <Route path="support"       element={<PortalSupport />} />
              <Route path="support/:id"   element={<PortalSupportDetail />} />
              <Route path="settings"      element={<PortalSettings />} />
            </Route>

            {/* Operator portal — authenticated. (Sign-in lives at the unified
                /sign-in route above; old /admin/sign-in URLs redirect there.) */}
            <Route
              path="/admin"
              element={
                <RequireAuth kind="staff" signInTo="/sign-in">
                  <AdminLayout />
                </RequireAuth>
              }
            >
              <Route index element={<AdminOverview />} />
              <Route path="inbox"        element={<AdminInbox />} />
              <Route path="support"      element={<AdminSupport />} />
              <Route path="support/:id"  element={<AdminSupportDetail />} />
              <Route path="requests/:id" element={<AdminRequestDetail />} />
              <Route path="pipeline"     element={<AdminPipeline />} />
              <Route path="clients"      element={<AdminClients />} />
              <Route path="clients/new"  element={<AdminClientCreate />} />
              <Route path="clients/:id"  element={<AdminClientDetail />} />
              <Route path="proposals"      element={<AdminProposals />} />
              <Route path="proposals/new"  element={<AdminProposalDetail />} />
              <Route path="proposals/:id"  element={<AdminProposalDetail />} />
              <Route path="finance"   element={<AdminFinance />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="invoices/:id" element={<AdminInvoiceDetail />} />
              <Route path="reports"   element={<AdminReports />} />
              <Route path="kyc"       element={<Navigate to="/admin/compliance/register" replace />} />
              <Route path="compliance" element={<Navigate to="/admin/compliance/register" replace />} />
              <Route path="compliance/queue" element={<ComplianceQueue />} />
              <Route path="compliance/register" element={<AdminKyc />} />
              <Route path="compliance/clients/:id" element={<ComplianceClient />} />
              <Route path="cms"       element={<PortalStub title="CMS · Content" subtitle="Pages, services, insights, templates, legal, branding, flags." />} />
              <Route path="users"     element={<AdminUsers />} />
              <Route path="audit"     element={<AdminAudit />} />
              <Route path="settings"  element={<PortalStub title="Settings" subtitle="Operator preferences." />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </MarketingChrome>
        {/* Rendered once, app-wide — visible on public, portal and admin. */}
        <CookieBanner />
        <CookiePreferenceCenter />
        </ConsentProvider>
      </LegalProvider>
    </AuthProvider>
  );
}
